/**
 * First-party event sink for the product-ranker training pipeline.
 *
 * In-memory queue → batched keepalive POST to the `theaWebLogEvents` callable
 * → GCS → hourly bq-load → BigQuery `analytics.user_events`.
 *
 * Why first-party (not GA4): ad-blockers drop 25-40% of GA events with strong
 * selection bias toward privacy-conscious users; the resulting training data
 * is biased and unfixable. Same-origin POST through Firebase Functions runs on
 * our own domain and survives blockers ~100% of the time.
 *
 * Why a raw `fetch` with `keepalive: true` instead of the httpsCallable
 * wrapper: mobile click-outs navigate to the merchant IN THE SAME TAB
 * (`openExternal` → `window.location.assign`), which unloads the document
 * milliseconds after `product_clicked` is queued. A plain fetch in flight at
 * unload is aborted by the browser, so the pre-keepalive transport silently
 * lost ~every mobile click-out (confirmed against BQ 2026-07-14: all 85
 * `product_clicked` rows ever recorded came from desktop sessions).
 * `keepalive` hands the request to the browser's network stack, which
 * completes it after the document is gone. The POST speaks the callable
 * protocol (`{data: ...}` body, `Bearer` ID token) so the BE handler is
 * unchanged.
 *
 * Flush triggers:
 *   - 10 events queued (BATCH_SIZE)
 *   - 5s elapsed since first queued event (BATCH_INTERVAL_MS)
 *   - `visibilitychange → hidden` (mobile background, tab switch)
 *   - `pagehide` (true unload)
 *
 * The unload-path flush works ONLY because everything up to the fetch
 * dispatch is synchronous: batch snapshot, URL construction, and the ID
 * token (kept in a module cache by an `onIdTokenChanged` listener — an
 * `await` during unload never resumes). Keep it that way.
 *
 * Bot traffic is dropped at queue time (mirrors gaPixel's posture). Errors
 * never bubble — telemetry must not break the user's session.
 *
 * One thing is held rather than dropped: a batch that finds no ID token. That
 * is auth not having landed YET, not a failure, and dropping it made the sink
 * blind to pre-auth events — see postBatch. Everything else still drops.
 */

import { onIdTokenChanged } from 'firebase/auth';

import { getAppInstance, getAuthInstance, USE_EMULATOR } from '../../firebaseConfig';
import { isSuppressedSession, updateFromClaims } from '../auth/impersonation';
import type { TheaWebLogEvent, TheaWebLogEventName } from '../schemas';
import { isBot } from './botDetect';

const BATCH_SIZE = 10;
const BATCH_INTERVAL_MS = 5_000;
// Keepalive requests share a hard 64KiB in-flight body quota per page
// (Chrome rejects the fetch outright above it). Keep every batch comfortably
// under the quota so the unload-path flush is never refused for size — this
// FE budget is the binding one, far below the BE's 256KB cap.
const PAYLOAD_BYTE_BUDGET = 48 * 1024;
// A batch that finds no ID token is put back, not dropped (see postBatch), so
// the queue has to have a ceiling or a tab where auth never resolves grows it
// without bound. On overflow the OLDEST events go: the queue is a ring buffer,
// which keeps the sink's loss recency-biased rather than letting one stuck
// session pin 2026-era events in memory forever. 200 is ~20 full batches —
// far above the handful of events that accumulate during a real anonymous
// sign-up, including on the 3G clients this file's callers care about.
const QUEUE_MAX = 200;

let queue: TheaWebLogEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersInstalled = false;
// Synchronously-readable ID token for the unload-path flush. Kept fresh by
// onIdTokenChanged (fires immediately with the current user and again on
// every SDK-driven refresh, so it tracks the live token all session).
let cachedIdToken: string | null = null;

function eventTsClient(): string {
  // performance.timeOrigin is wall-clock ms at navigation start; .now() is
  // monotonic ms since. The sum is a high-resolution wall-clock timestamp
  // that doesn't drift even if the system clock changes mid-session.
  if (typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number') {
    return new Date(performance.timeOrigin + performance.now()).toISOString();
  }
  return new Date().toISOString();
}

function newEventId(): string {
  // Prefer crypto.randomUUID where available; fall back to a sufficient
  // pseudo-random for older browsers (training-data dedup, not security).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Pseudo-uuid: timestamp + 16 random hex chars. Uniqueness is per-client,
  // and the BE dedup MERGE keys on event_id, so collisions across clients
  // are functionally impossible at our scale.
  const rand = Math.random().toString(16).slice(2, 18).padEnd(16, '0');
  return `${Date.now().toString(16)}-${rand}`;
}

function logEventsUrl(): string {
  const projectId = getAppInstance().options.projectId;
  if (USE_EMULATOR) {
    // Mirrors connectFunctionsEmulator in firebaseFunctions.ts.
    return `http://127.0.0.1:5001/${projectId}/us-central1/theaWebLogEvents`;
  }
  return `https://us-central1-${projectId}.cloudfunctions.net/theaWebLogEvents`;
}

function ensureFlushTimer(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, BATCH_INTERVAL_MS);
}

function clearFlushTimer(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

function installLifecycleListenersOnce(): void {
  if (listenersInstalled) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  listenersInstalled = true;

  // Hidden → flush. Fires on tab switch and on Safari's pre-unload moment.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flush();
    }
  });

  // Hard unload. The keepalive fetch dispatched here survives the document —
  // this is the leg that records mobile same-tab click-outs.
  window.addEventListener('pagehide', () => {
    void flush();
  });

  // Prime and track the token cache. Guarded separately: if auth is
  // unavailable (tests, Storybook without config) the sink still queues and
  // the flush falls back to awaiting a token on the non-unload paths.
  //
  // getIdTokenResult, not getIdToken: the sink's impersonation gate must be
  // airtight (an impersonated session's impressions would feed the target's
  // suggestion-profile seen-demotion), so the `impersonator` claim is learned
  // in the SAME resolution that hands us the token — a batch can never be
  // posted under a token whose claims we haven't folded into the gate.
  try {
    onIdTokenChanged(getAuthInstance(), (user) => {
      if (!user) {
        cachedIdToken = null;
        return;
      }
      user.getIdTokenResult().then(
        (r) => {
          updateFromClaims(r.claims);
          cachedIdToken = r.token;
        },
        () => {},
      );
    });
  } catch {
    // Best-effort — see flush()'s cold-cache fallback.
  }
}

/**
 * Queue a single event for batched delivery. Safe to call from render-path
 * code: the actual POST is deferred to the next batch boundary, never
 * blocks, and never throws.
 *
 * Wrapped in a top-level try so a misbehaving caller (e.g. a property bag
 * that overrides toString to throw) can never bubble up into a click handler
 * or render path. Telemetry loss > user-facing breakage.
 */
export function logEvent(
  name: TheaWebLogEventName,
  properties: Record<string, unknown> = {},
): void {
  try {
    if (typeof window === 'undefined') return; // SSR/test guard.
    if (isBot()) return;
    // Admin impersonation (?becomeToken=) and test accounts (`testUser`
    // claim): the session emits nothing — these rows would land in
    // analytics.user_events under a uid whose suggestion profile they would
    // poison, and a test account's rows are ranker-training noise besides.
    // flush() backstops events queued before the status resolved.
    if (isSuppressedSession()) return;

    installLifecycleListenersOnce();

    queue.push({
      event_id: newEventId(),
      event_name: name,
      event_ts_client: eventTsClient(),
      properties,
    });

    if (queue.length >= BATCH_SIZE) {
      void flush();
    } else {
      ensureFlushTimer();
    }
  } catch {
    // Best-effort. A throw here would propagate into the caller's render
    // path or click handler, which we explicitly never want.
  }
}

/**
 * POST one batch through the callable protocol. With a warm token cache the
 * fetch is dispatched synchronously (no await before it) — required for the
 * unload path. Cold cache (events queued before auth resolved) awaits the
 * token: fine on timer/size flushes; on an unload flush the await never
 * resumes and the batch is lost — same loss mode as the old transport,
 * never worse.
 *
 * Returns 'deferred' — never having touched the network — when there is no
 * identity yet. That is a WAIT, not a failure: `ensureAuth()`'s anonymous
 * sign-up is in flight and a token is seconds away, so the caller re-queues
 * the batch instead of dropping it. Dropping here biased the sink against
 * exactly the pre-auth slow clients whose banner impressions (THE-524) are
 * the reason this telemetry exists — a `geo_resolved: false` rate read off a
 * sink that silently discards early events reads low no matter how bad the
 * real number is.
 */
type BatchOutcome = 'sent' | 'deferred';

/**
 * Dev-only: shout when the BE told us it threw some of the batch away.
 *
 * `theaWebLogEvents` validates `event_name` against its own allowlist in
 * thea-serverless and counts anything unrecognised into `rejected` while
 * still returning 200. Nothing read that number, so the two-sided contract
 * failed open-looking and closed at the same time: `product_similar_requested`
 * shipped FE-only on 2026-09-21 and every tap was dropped for three days with
 * no client-visible symptom and nothing in any log (THE-570). It was the third
 * name to hit this — `consent_banner_impression` and `hint_committed` too.
 *
 * The BE reports a count, not which names it disliked, so we list the distinct
 * names in the batch: with a batch of 10 that is a short list to eyeball, and
 * a name that has only just been added to `TheaWebLogEventName` is the obvious
 * suspect. Deliberately dev-only — in prod this is the BE's WARNING line to
 * emit, and a console.warn on a real user's session buys nothing. CRA inlines
 * `process.env.NODE_ENV`, so the whole block is dead-code-eliminated from the
 * production bundle.
 *
 * Fire-and-forget on purpose: the caller must not wait on the response body.
 * Reading it is an await AFTER the fetch has been dispatched, so the unload
 * path is unaffected — on unload it simply never resumes, which is fine.
 */
function warnOnRejectedInDev(res: Response, batch: TheaWebLogEvent[]): void {
  if (process.env.NODE_ENV === 'production') return;
  void (async () => {
    try {
      // Callable protocol: the handler's return value is under `result`.
      const rejected = (await res.json())?.result?.rejected;
      if (typeof rejected !== 'number' || rejected <= 0) return;
      const names = Array.from(new Set(batch.map((e) => e.event_name))).sort();
      console.warn(
        `[eventSink] theaWebLogEvents dropped ${rejected}/${batch.length} event(s). ` +
          `Names in this batch: ${names.join(', ')}. A name missing from ` +
          `ALLOWED_EVENT_NAMES in thea-serverless functions/theaWebMutations/` +
          `theaWebLogEvents.py is dropped silently behind a 200 — and it stays ` +
          `dropped until that change is DEPLOYED, not merged.`,
      );
    } catch {
      // Best-effort diagnostics. A body we can't parse is not worth a throw,
      // and this runs detached from the caller's promise chain, so an
      // unhandled rejection here would surface as a console error for nothing.
    }
  })();
}

async function postBatch(batch: TheaWebLogEvent[], body: string): Promise<BatchOutcome> {
  let token = cachedIdToken;
  if (token === null) {
    // Same claims-with-token coupling as the listener above — the cold-cache
    // fallback must not open a path to posting with unexamined claims.
    const result = (await getAuthInstance().currentUser?.getIdTokenResult()) ?? null;
    if (result === null) return 'deferred'; // No identity yet — hold, don't drop.
    updateFromClaims(result.claims);
    token = result.token;
    cachedIdToken = token;
  }
  // 'sent' as in "disposed of": a suppressed session's batch (impersonation
  // or test account) is dropped, never re-queued and never posted.
  if (isSuppressedSession()) return 'sent';
  const res = await fetch(logEventsUrl(), {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body,
  });
  warnOnRejectedInDev(res, batch);
  return 'sent';
}

/**
 * Serialize a batch and POST it, splitting in half when it exceeds the
 * keepalive byte budget — much simpler than per-event accounting and covers
 * the realistic case (a burst of events with large `properties`). Recursing
 * on explicit halves (not via re-queue + flush) is what guarantees
 * termination: each level strictly shrinks, bottoming out at single events.
 * The stringified body is reused for the POST so the size we measured is the
 * size we send. On an unload flush only the first leaf's dispatch is
 * guaranteed — acceptable: a >48KB burst at the exact unload moment is
 * vanishingly rare.
 *
 * Returns the events that were NOT delivered and should be held for the next
 * flush — i.e. the leaves that came back 'deferred' for want of a token.
 * Everything else returns empty, including outright failures: a rejected
 * fetch still drops, unchanged.
 */
async function sendOrSplit(batch: TheaWebLogEvent[]): Promise<TheaWebLogEvent[]> {
  let body = '';
  try {
    body = JSON.stringify({ data: { events: batch } });
  } catch {
    // If we can't even stringify the batch, the BE call would fail too.
    // Drop the batch silently rather than retry.
    return [];
  }
  if (body.length > PAYLOAD_BYTE_BUDGET && batch.length > 1) {
    const mid = Math.floor(batch.length / 2);
    const left = await sendOrSplit(batch.slice(0, mid));
    const right = await sendOrSplit(batch.slice(mid));
    return [...left, ...right];
  }
  try {
    return (await postBatch(batch, body)) === 'deferred' ? batch : [];
  } catch {
    // Telemetry must never break the user. Drop on TRANSPORT failure — the BE
    // can't reproduce this batch and there's no productive retry path that
    // won't amplify the failure. A missing token is not a transport failure
    // and does not come through here; postBatch reports it as 'deferred'
    // before touching the network. Future: consider sessionStorage spool for
    // retry on next page load if loss rate proves significant.
    return [];
  }
}

/**
 * Flush all queued events immediately. Called on lifecycle events and from
 * tests; callers shouldn't normally need to invoke this directly. Returns a
 * promise that resolves when the call completes (or fails); the resolved
 * promise is provided for tests, not for caller awaiting.
 */
export async function flush(): Promise<void> {
  try {
    clearFlushTimer();
    // Suppressed session (impersonation or test account): discard everything,
    // including events queued while the status was still unknown (the
    // arrival/reload marks make that window tiny, and postBatch's
    // claims-with-token coupling closes it entirely).
    if (isSuppressedSession()) {
      queue = [];
      return;
    }
    if (queue.length === 0) return;

    // Take a snapshot so events queued during the in-flight POST land in the
    // next batch instead of being dropped.
    const batch = queue;
    queue = [];

    const deferred = await sendOrSplit(batch);
    if (deferred.length > 0) {
      // No token yet — put the batch back and re-arm the timer, so the next
      // attempt happens on its own rather than waiting for the next
      // logEvent() call. A page whose only event is the banner impression
      // would otherwise sit on it forever.
      //
      // Re-sort on client timestamp rather than prepending: several flushes
      // can be in flight at once (a size flush overlapping a
      // visibilitychange one), each holding a snapshot taken at a different
      // moment, so "what I hold is older than what is in the queue" is not
      // true in general. `event_ts_client` is always a UTC ISO string, so
      // lexicographic order IS chronological order. This is what makes the
      // QUEUE_MAX eviction below drop the genuinely oldest events.
      queue = [...deferred, ...queue]
        .sort((a, b) => a.event_ts_client.localeCompare(b.event_ts_client))
        .slice(-QUEUE_MAX);
      ensureFlushTimer();
    }
  } catch {
    // Outer safety net — anything we missed (a synchronous throw from
    // fetch() before it returns its promise, an exception from the queue
    // snapshot, etc.) gets swallowed here.
  }
}

/** Test-only: drain state between tests. Never call from app code. */
export function _resetEventSinkForTesting(): void {
  queue = [];
  clearFlushTimer();
  listenersInstalled = false;
  cachedIdToken = null;
}

/** Test-only: peek the current queue without flushing. */
export function _peekQueueForTesting(): TheaWebLogEvent[] {
  return [...queue];
}
